import { MagicWand } from "components/icons/MagicWand";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { SectionHeader, SettingRow } from "./BooksSettings";
import { SettingsAccordion } from "./SettingsAccordion";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import { formatDate } from "utils/formatDate";
import {
  MANAGE_ACCOUNTS_PAYABLE,
  MANAGE_ACCOUNTS_RECEIVABLE,
} from "constants/subscriptionPermissionFeatures";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { Button } from "components/DesignSystem/Button/Button";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { BooksLockDateResponse } from "store/apis/booksLockDate";
import { ModalControl } from "hooks/useModal";

const AutomationSettings = ({
  lockDateData,
  subscriptionModal,
  handleAccountsReceivableToggle,
  handleAccountsPayableToggle,
}: {
  lockDateData: BooksLockDateResponse | undefined;
  subscriptionModal: ModalControl;
  handleAccountsReceivableToggle: (checked: boolean) => void;
  handleAccountsPayableToggle: (checked: boolean) => void;
}) => {
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.ACCOUNTING
  );

  return (
    <>
      <SettingsAccordion.Item value="AUTOMATION">
        <SettingsAccordion.Trigger>
          <SectionHeader
            icon={<MagicWand className="t-size-3.5" />}
            title="Automation"
          />
        </SettingsAccordion.Trigger>

        <SettingsAccordion.Content>
          <SettingRow
            title="Auto-manage accounts receivable"
            description="Automatically create and clear receivable entries as invoices are raised and paid. Also activates Auto-Defer & Recognize Revenue — generating monthly revenue schedules from invoices for accurate revenue recognition."
            info={
              <>
                {lockDateData?.accounts_receivable_from_date &&
                  lockDateData?.accounts_receivable_enabled && (
                    <span className="t-text-body-sm t-text-text-30 t-italic">
                      Accounts Receivable Start Date:{" "}
                      {formatDate(lockDateData?.accounts_receivable_from_date)}
                    </span>
                  )}
                {!lockDateData?.accounts_receivable_enabled && (
                  <PermissionBasedUI
                    feature={MANAGE_ACCOUNTS_RECEIVABLE}
                    blockedUI={
                      <p className="t-text-body-sm t-text-neutral-40 t-flex t-items-center t-gap-1 t-m-0 t-italic">
                        <DiamondPurple currentColor="currentColor" />
                        <span>
                          This is a Books Standard plan exclusive feature
                        </span>
                      </p>
                    }
                  >
                    <></>
                  </PermissionBasedUI>
                )}
              </>
            }
          >
            <PermissionBasedUI
              feature={MANAGE_ACCOUNTS_RECEIVABLE}
              onUpgrade={subscriptionModal.open}
              blockedUI={
                <Button
                  size="small"
                  disabled
                  leftIcon={<DiamondPurple height={12} width={12} />}
                >
                  Upgrade for access
                </Button>
              }
            >
              <Switch
                checked={Boolean(lockDateData?.accounts_receivable_enabled)}
                onCheckedChange={handleAccountsReceivableToggle}
                disabled={!hasWritePermission}
              />
            </PermissionBasedUI>
          </SettingRow>

          <SettingRow
            title="Auto-manage accounts payable"
            description="Automatically create payable entries when bills are raised and auto-categorize the corresponding transactions to clear the same when the bills are marked as paid."
            info={
              <>
                {lockDateData?.accounts_payable_enabled &&
                  lockDateData?.accounts_payable_from_date && (
                    <span className="t-text-body-sm t-text-text-30 t-italic">
                      Accounts Payable Start Date:{" "}
                      {formatDate(lockDateData?.accounts_payable_from_date)}
                    </span>
                  )}
                {!lockDateData?.accounts_payable_enabled && (
                  <PermissionBasedUI
                    feature={MANAGE_ACCOUNTS_PAYABLE}
                    blockedUI={
                      <p className="t-text-body-sm t-text-neutral-40 t-flex t-items-center t-gap-1 t-m-0 t-italic">
                        <DiamondPurple currentColor="currentColor" />
                        <span>
                          This is a Books Standard plan exclusive feature
                        </span>
                      </p>
                    }
                  >
                    <></>
                  </PermissionBasedUI>
                )}
              </>
            }
          >
            <PermissionBasedUI
              feature={MANAGE_ACCOUNTS_PAYABLE}
              onUpgrade={subscriptionModal.open}
              blockedUI={
                <Button
                  size="small"
                  disabled
                  leftIcon={<DiamondPurple height={12} width={12} />}
                >
                  Upgrade for access
                </Button>
              }
            >
              <Switch
                checked={Boolean(lockDateData?.accounts_payable_enabled)}
                onCheckedChange={handleAccountsPayableToggle}
                disabled={!hasWritePermission}
              />
            </PermissionBasedUI>
          </SettingRow>
        </SettingsAccordion.Content>
      </SettingsAccordion.Item>
    </>
  );
};

export default AutomationSettings;
